import * as React from 'react';
import { create } from 'zustand';
import type { PiletApi } from '@chess/platform-web';
import { CHESS_API, uciToInput, type Chess } from '@chess/api-chess';
import { STORE_API, type GameRecord, type GameStore } from '@chess/api-model';
import { registerAggregator, useAggregate, useStoreQuery } from '@chess/api-model/react';
import { memoisedMotifs, MOTIFS_API, type Motif, type MotifOf, type Motifs } from '@chess/api-mistakes';
import { Skeleton } from '@chess/api-skeleton';
import { useNavigate } from 'react-router';
import { GAMES_API, type Games } from '@chess/api-games';
import { ANALYSIS_API, type Analysis, type AnalysisEvent } from '@chess/api-analysis';
import { REVIEW_API, type Review, type ReviewState } from '@chess/api-review';
import { CONSTRAINTS_API, ownerId, type ConstraintRegistry } from '@chess/api-constraints';
import { createPuzzleOwner } from './owner.ts';
import { ENGINE_API, type Engine } from '@chess/api-engine';
import { TOAST_API, type Toast } from '@chess/api-toast';
import { LOG_API, type Log } from '@chess/api-log';
import { OPENINGS_API, type Openings } from '@chess/api-openings';
import { describePrize, puzzleForMove, type Puzzle } from './finder.ts';
import { PUZZLES_API, type PuzzleStatus, type Puzzles } from '@chess/api-puzzles';
import { recordAttempt } from './files.ts';
import { createSolver } from './solving.ts';
import { createListLoader, finderRules, type ListLoader, type PuzzleState } from './list-loader.ts';
import { PuzzleCard } from './PuzzleCard.tsx';
import { PuzzleBanner } from './PuzzleBanner.tsx';
import { bannerOf, gameLineOf } from './banner.ts';
import { dotsOf, playedLine } from './dots.ts';
import { seriesOf } from './series.ts';
import { createMotifReader, motifPhrase, type MotifReader } from './motif.ts';
import { LearnCard } from './LearnCard.tsx';
import { countLine, learnTitle } from './learn.ts';
import { motifCount, MotifCountsAggregator } from './MotifCountsAggregator.ts';
import { useSelect } from '@chess/api-review/react';
import { CORRECT_FLASH_MS, cardOf, flashes, memoryOf, promptOf, type FlashMemory, clicksBlocked } from './card.ts';
import { badges as phaseBadges, statusOf as puzzleStatusOf } from './status.ts';
import { createAddressPuzzle, withoutPuzzle } from './address.ts';
import './puzzles.css';

// The puzzles of a reviewed game, shown by phase. Two kinds come out of a
// game: the forcing wins the reader missed, and the blunders replayed as
// "find the better move". The list is made from the stored scan the first
// time any phase tab shows its puzzles for a game and kept in the
// plugin's own folder; each phase tab's badge carries its own count.
// Attempts are kept apart from the list. There is no Puzzles tab of its
// own: each phase tab declares a sections slot, and this plugin fills it
// with the stage's table of blunders and misses, each row opening a puzzle.

const SLOT = 'review.sidePanel';

const usePuzzles = create<PuzzleState>(() => ({ status: 'idle', scanned: 0, total: 0, puzzles: [], attempts: {} }));
/** The motif reader (motif.ts) over the `mistakes.motifs` service, once it is at hand, with the service itself for the Learn card's pages. */
const useMotifs = create<{ read?: MotifReader; motifs?: Motifs }>(() => ({}));

/** The Learn card's shape while the motif resolves: the same card with sample data, so nothing moves when it arrives. */
const LEARN_SHAPE = (
  <LearnCard
    title="Learn: Pin · against the queen"
    references={[
      { title: 'Lichess Practice: The Pin', url: 'https://lichess.org/', source: 'lichess', kind: 'lesson', summary: 'Interactive drills on pinning a piece to a more valuable one behind it.' },
      { title: 'Pin (chess) – Wikipedia', url: 'https://en.wikipedia.org/', source: 'wikipedia', kind: 'article', summary: 'The pin: a piece that cannot move without exposing a more valuable one.' },
      { title: 'Pin – Chess.com terms', url: 'https://www.chess.com/', source: 'chesscom', kind: 'article', summary: 'Absolute and relative pins, with examples.' },
    ]}
    count={{ text: '3 of your blunders in the last 30 days were a pin', href: '/library' }}
  />
);
const NO_SERIES: readonly number[] = [];

export function setup(app: PiletApi) {
  const getApi = <T,>(name: string) => app.getApi<T>(name);
  let log: Log | undefined;
  void app.getApi<Log>(LOG_API).then((l) => (log = l.child(app.meta.name)));

  const apis = () =>
    Promise.all([
      app.getApi<Games>(GAMES_API),
      app.getApi<Chess>(CHESS_API),
      app.getApi<Analysis>(ANALYSIS_API),
      app.getApi<Review>(REVIEW_API),
      app.getApi<Engine>(ENGINE_API),
    ]);

  /** Each phase tab's badge (status.ts), emitted to the tabs. */
  const badges = (puzzles: Puzzle[] | undefined, state: ReviewState | undefined) => {
    for (const { id, badge } of phaseBadges(puzzles, state)) app.emit('review.tabBadge', { slot: SLOT, id, badge });
  };

  let reviewApi: Review | undefined;

  // The list's reading and making (list-loader.ts), over the APIs once
  // resolved, the first time it is needed. The openings API is optional:
  // without it, or if it is slow, no puzzle is flagged as in the opening.
  let lists: Promise<ListLoader> | undefined;
  const listsFor = () =>
    (lists ??= apis().then(([games, chess, analysis]) =>
    createListLoader({
      games,
      chess,
      analysis,
      openings: () =>
        Promise.race([app.getApi<Openings>(OPENINGS_API), new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), 2000))]),
      log: { info: (message, fields) => log?.info(message, fields), warn: (message, fields) => log?.warn(message, fields) },
      store: usePuzzles,
      onReady: (puzzles) => badges(puzzles, reviewApi?.state()),
    }),
  ));
  const readStored = (gameId: string) => listsFor().then((loader) => loader.readStored(gameId));

  // The counts in the phase tabs' titles come from the stored list whenever
  // a game opens, without generating anything; generation waits for a
  // phase section to show.

  void app.getApi<Review>(REVIEW_API).then((review) => {
    reviewApi = review;
    let shown: string | undefined;
    // The stored list of the open game, once read, and whether its counts
    // have been emitted against a loaded game: the record can still be
    // loading when the list is read, and the counts need its phases.
    let stored: { gameId: string; puzzles?: Puzzle[] } | undefined;
    let badgedFor: string | undefined;
    const emitIfLoaded = (state: ReviewState) => {
      if (!state.game || !state.gameId || stored?.gameId !== state.gameId || badgedFor === state.gameId) return;
      badges(stored.puzzles, state);
      badgedFor = state.gameId;
    };
    // A `?puzzle=` in the address starts that puzzle once the game is in,
    // then leaves the address (address.ts).
    const addressed = createAddressPuzzle({
      search: () => window.location.search,
      clear: () => window.history.replaceState(window.history.state, '', withoutPuzzle(window.location.href)),
      start: (ply) => startForMove(ply),
    });
    const follow = (state: ReviewState) => {
      if (state.gameId === shown) {
        emitIfLoaded(state);
        addressed.follow(state);
        return;
      }
      shown = state.gameId;
      addressed.opened(state);
      stored = undefined;
      badgedFor = undefined;
      if (usePuzzles.getState().gameId !== state.gameId) {
        usePuzzles.getState().solving && solver?.stop();
        usePuzzles.setState({ gameId: state.gameId, status: 'idle', puzzles: [], attempts: {}, solving: undefined });
      }
      if (!state.gameId) {
        badges(undefined, undefined);
        return;
      }
      const gameId = state.gameId;
      void readStored(gameId).then(({ list, attempts }) => {
        if (usePuzzles.getState().gameId !== gameId) return;
        if (list && usePuzzles.getState().status === 'idle') {
          usePuzzles.setState({ status: 'ready', puzzles: list.puzzles, attempts: attempts.attempts });
        }
        stored = { gameId, puzzles: list?.puzzles };
        emitIfLoaded(review.state());
      });
    };
    follow(review.state());
    review.subscribe(follow);
  });

  // Scan progress, shown while a section waits for a complete scan.
  void app.getApi<Analysis>(ANALYSIS_API).then((analysis) =>
    analysis.subscribe((event: AnalysisEvent) => {
      const s = usePuzzles.getState();
      if (event.gameId !== s.gameId || s.status !== 'scanning') return;
      if (event.type === 'progress') usePuzzles.setState({ scanned: event.done, total: event.total });
    }),
  );

  let solver: ReturnType<typeof createSolver> | undefined;
  const solverFor = async () => {
    if (solver) return solver;
    const [, chess, analysis, review, engine] = await apis();
    const loader = await listsFor();
    const toast = await app.getApi<Toast>(TOAST_API).catch(() => undefined);
    const registry = await Promise.race([
      app.getApi<ConstraintRegistry>(CONSTRAINTS_API),
      new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), 3000)),
    ]);
    // The puzzle's constraints on its board (owner.ts); the solver's own steps
    // run as their owner, so the board's enforcement lets them by.
    const owner = createPuzzleOwner(registry, review, () => solver?.abandon(), ownerId(app.meta.name));
    solver = createSolver({
      review: owner.review,
      engine,
      analysis,
      // The rules of chess, so Show solution walks the scan's line (solution-line.ts).
      chess,
      onChange: (solving) => {
        // The moment of solving, once: the board marks the move for a beat (review-board).
        const before = usePuzzles.getState().solving;
        if (solving?.status === 'solved' && solving.solvedMove && before?.status !== 'solved') app.emit('review.puzzleSolved', { uci: solving.solvedMove });
        if (solving?.status === 'failed' && solving.wrong && before?.status !== 'failed') app.emit('review.puzzleFailed', { uci: solving.wrong });
        usePuzzles.setState({ solving });
        owner.constrain(solving, promptOf(solving));
      },
      onResult: (puzzle, status, tries) => {
        const gameId = usePuzzles.getState().gameId;
        if (!gameId) return;
        void recordAttempt(loader.folderOf(gameId), puzzle.id, status, tries).then((all) => {
          if (usePuzzles.getState().gameId === gameId) usePuzzles.setState({ attempts: all.attempts });
        });
        toast?.show(status === 'solved' ? `Solved: ${describePrize(puzzle.prize)}` : 'Not that move. It is taken back in a moment.', {
          kind: status === 'solved' ? 'info' : 'warning',
        });
        log?.info('puzzle attempt', { gameId, puzzle: puzzle.id, status, tries });
      },
    });
    return solver;
  };

  // The best-moves place's puzzle case: the container (review-shortlist-theory)
  // renders this slot instead of the theory or engine lines while a puzzle is open.
  // A correct step flashes the card green for a moment (`flashes`, card.ts).
  // The game's series of puzzles (series.ts): the reader's blunders and misses
  // by the review's rule, read live off the session so the banner, the dots
  // and the stage panels' rows agree. Empty while nothing is read.
  const useSeries = () => useSelect(reviewApi, (s) => (s.game ? seriesOf(s.scan, s.game.moves, s.reader) : [])) ?? NO_SERIES;

  // The puzzle's motif (motif.ts): the motif of the mistake it was made from,
  // through the `mistakes.motifs` service, from the store's fact for the game
  // when it holds one (the last 30 days) and else from the review's scan;
  // classified once per game and ply, never in render. Undefined until the
  // service is at hand, for the opening's theory puzzle, and while the scan
  // has not read the positions around the move.
  // The Learn card's count (MotifCountsAggregator.ts) is registered with the
  // store once the service is at hand, the classifier memoised per fact.
  let motifOf: MotifOf | undefined;
  const counts = new MotifCountsAggregator((m, reader) => motifOf!(m, reader));
  void app.getApi<Motifs>(MOTIFS_API).then((motifs) => {
    motifOf = memoisedMotifs(motifs);
    useMotifs.setState({ read: createMotifReader(motifs), motifs });
    registerAggregator(getApi, counts);
  });
  const useMotifOf = (puzzle: Puzzle | undefined): Motif | undefined => {
    const read = useMotifs((s) => s.read);
    const gameId = usePuzzles((s) => s.gameId);
    const facts = useStoreQuery(getApi, (store) => (gameId ? store.game(gameId)?.facts?.mistakes : undefined), [gameId]);
    const scan = useSelect(reviewApi, (s) => s.scan);
    const game = useSelect(reviewApi, (s) => s.game);
    const reader = useSelect(reviewApi, (s) => s.reader);
    return React.useMemo(
      () => (read && gameId && puzzle ? read(gameId, puzzle, { facts, scan, game, reader }) : undefined),
      [read, gameId, puzzle, facts, scan, game, reader],
    );
  };

  // The Learn card (LearnCard.tsx, learn.ts) under the puzzle card: the
  // puzzle's motif, the service's pages for it, and the reader's count of
  // the family over the period from the store. A skeleton while the motif
  // resolves; nothing for the opening's theory puzzle, which has no motif.
  const LearnPlace: React.FC<{ puzzle: Puzzle; motif: Motif | undefined }> = ({ puzzle, motif }) => {
    const motifs = useMotifs((s) => s.motifs);
    const { result } = useAggregate(getApi, counts);
    const navigate = useNavigate();
    const references = React.useMemo(() => (motif && motifs ? motifs.referencesFor(motif) : []), [motif, motifs]);
    if (puzzle.kind === 'theory') return null;
    const kind = puzzle.kind === 'tactic' ? 'miss' : 'blunder';
    return (
      <Skeleton loading={!motif} shape={LEARN_SHAPE} label="Loading what to learn">
        {motif && (
          <LearnCard title={learnTitle(motif)} references={references} count={countLine(kind, motif.family, motifCount(result, kind, motif.family))} onOpenCount={(href) => navigate(href)} />
        )}
      </Skeleton>
    );
  };

  const PuzzlePlace: React.FC = () => {
    const solving = usePuzzles((s) => s.solving);
    const series = useSeries();
    const attempts = usePuzzles((s) => s.attempts);
    const motif = useMotifOf(solving?.puzzle);
    const [correct, setCorrect] = React.useState(false);
    const last = React.useRef<FlashMemory>({ applied: 0 });
    React.useEffect(() => {
      if (!solving) return;
      const before = last.current;
      last.current = memoryOf(solving);
      if (!flashes(before, solving)) return;
      setCorrect(true);
      const timer = setTimeout(() => setCorrect(false), CORRECT_FLASH_MS);
      return () => clearTimeout(timer);
    }, [solving?.puzzle.id, solving?.applied, solving?.status]);
    if (!solving) return null;
    // The game's own move at the puzzle's ply, for the line once it is over (dots.ts).
    const playedSan = reviewApi?.state().game?.moves[solving.puzzle.ply]?.san;
    return (
      <div className="puzzle-place">
        {/* Every click blocked from the solving move until the line has played out (card.ts). */}
        {clicksBlocked(solving) && <div className="puzzle-shield" aria-hidden="true" />}
        <PuzzleCard
          {...cardOf(solving)}
          motif={motif && motifPhrase(motif)}
          correct={correct}
          dots={dotsOf(series, solving.puzzle, attempts)}
          played={playedLine(solving.puzzle, playedSan)}
          onRetry={() => solver?.retry()}
          onStop={() => solver?.stop()}
          onShow={() => solver?.showSolution()}
        />
        <LearnPlace puzzle={solving.puzzle} motif={motif} />
      </div>
    );
  };
  app.registerExtension('review.shortlist.puzzle', PuzzlePlace);

  // The puzzle banner (banner.ts, PuzzleBanner.tsx) in the game bar's row
  // while a puzzle is on the board: the game bar yields the row to the
  // `review.header.puzzle` slot while `puzzles.current()` is set. The game's
  // record, for the "White vs Black · date" line, is read once per game.
  let chessApi: Chess | undefined;
  void app.getApi<Chess>(CHESS_API).then((c) => (chessApi = c));
  let store: GameStore | undefined;
  void app.getApi<GameStore>(STORE_API).then((s) => (store = s));
  const useRecord = create<{ gameId?: string; record?: GameRecord }>(() => ({}));
  const recordFor = (gameId: string | undefined) => {
    if (useRecord.getState().gameId === gameId) return;
    useRecord.setState({ gameId, record: undefined });
    if (!gameId || !store) return;
    void store
      .record(gameId)
      .then((record) => useRecord.getState().gameId === gameId && useRecord.setState({ gameId, record }))
      .catch(() => undefined);
  };
  /** The first move of the answer in SAN: the stored line's for an engine puzzle, the step's first answer for the opening's. */
  const bestSanOf = (puzzle: Puzzle): string | undefined => {
    if (puzzle.prize.kind === 'theory') return puzzle.prize.steps[0]?.answers[0];
    const first = puzzle.line[0];
    return first ? chessApi?.play(puzzle.fen, uciToInput(first))?.move.san : undefined;
  };
  const Banner: React.FC = () => {
    const solving = usePuzzles((s) => s.solving);
    const series = useSeries();
    const gameId = usePuzzles((s) => s.gameId);
    const record = useRecord((s) => (s.gameId === gameId ? s.record : undefined));
    const motif = useMotifOf(solving?.puzzle);
    React.useEffect(() => recordFor(gameId), [gameId]);
    if (!solving) return null;
    const text = bannerOf(solving, series, bestSanOf(solving.puzzle), motif && motifPhrase(motif));
    const next = text.next;
    return (
      <PuzzleBanner
        title={text.title}
        line={text.line}
        game={record ? gameLineOf(record) : undefined}
        done={text.done}
        onShow={text.canShow ? () => solver?.showSolution() : undefined}
        onRetry={text.canRetry ? () => solver?.retry() : undefined}
        onNext={next !== undefined ? () => (solver?.stop(), startForMove(next + 1)) : undefined}
        onStop={() => solver?.stop()}
      />
    );
  };
  app.registerExtension('review.header.puzzle', Banner);

  /**
   * The `puzzles` API: another plugin (the stage's blunders-and-misses
   * table, `review-stage-moments`) starts the puzzle for one of the reader's
   * moves and reads its record, without importing this plugin. The puzzle is
   * the stored one when the finder kept it, else one made on demand
   * (`puzzleForMove`), so every judged move can be practised.
   */
  const startForMove = (ply: number): void => {
    void Promise.all([app.getApi<Review>(REVIEW_API), app.getApi<Chess>(CHESS_API)]).then(([review, chess]) => {
      const s = review.state();
      const scan = s.scan?.scan;
      if (!s.game || !scan || !s.reader) return;
      const at = ply - 1;
      const stored = usePuzzles.getState().puzzles.find((p) => p.ply === at && p.kind !== 'theory');
      const puzzle = stored ?? puzzleForMove(scan, { moves: s.game.moves, positions: s.game.positions }, at, s.reader, finderRules(chess));
      if (puzzle) void solverFor().then((solver) => solver.start(puzzle));
    });
  };
  const statusOf = (ply: number): PuzzleStatus | undefined => {
    const s = usePuzzles.getState();
    return puzzleStatusOf(ply, s.solving, s.attempts);
  };
  const puzzlesApi: Puzzles = {
    startForMove,
    statusOf,
    current: () => {
      const solving = usePuzzles.getState().solving;
      return solving ? { ply: solving.puzzle.ply + 1 } : undefined;
    },
    subscribe: (listener) => usePuzzles.subscribe(() => listener()),
  };
  app.registerApi(PUZZLES_API, puzzlesApi);
}
